import { useEffect, useRef, useState, useMemo } from 'react';
import { allowOrWarn, Util } from '../../util/Util';
import ConfirmDialog from '../../components/ConfirmDialog';
import ProductionModal from './ProductionModal';
import ProductionReport from './ProductionReport';
import { useAuth } from '../../hooks/useAuth';
import { useData } from '../../hooks/useData';
import { useDateRange, periodRequestConfig } from '../../hooks/useDateRange';
import { useUI } from '../../providers/MoleUIContext';
import { toast } from 'react-toastify';
import {
	Box,
	Chip,
	IconButton,
	ListItemIcon,
	MenuItem,
	Tooltip,
} from '@mui/material';
import {
	AddCircleOutline,
	CancelOutlined,
	CheckCircleOutline,
	Delete,
	DeleteOutline,
	DoneAll,
	Edit,
	PlayArrow,
	Refresh,
	Visibility,
} from '@mui/icons-material';
import {
	DATE_FILTER_OPTIONS,
	LocalStorageKeys,
	NUMBER_FILTER_OPTIONS,
	TEXT_FILTER_OPTIONS,
} from '../../util/constants';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TableView from '../../components/TableView';
import { MRT_ColumnDef } from 'material-react-table';
import { parseISODay } from "../../util/businessClock";
import { canAmend, canDrop, canRecord } from '../../util/access';

const statusChipColor = (status: string) => {
	if (status === 'FINALIZED') return 'success';
	if (status === 'RUNNING') return 'warning';
	return 'info';
};

const ProductionRecords = () => {
	const [toEdit, setToEdit] = useState<any>(null);
	const [toFinalize, setToFinalize] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [toView, setToView] = useState<any>(null);
	const [newRecord, setNewRecord] = useState(false);

	const { api, access } = useAuth();
	const { setLoadingMessage, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading } =
		useData(LocalStorageKeys.PRODUCTION_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(LocalStorageKeys.PRODUCTION_RECORDS);

	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		if (!loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [period]);

	const fetchData = async (useLoadModal = true, forceUpdate = false) => {
		setLoading(true);
		const res = await api.get('productions', {
			...periodRequestConfig(period),
		});
		if (res?.status === 200) {
			saveFromServer(res.data, period, forceUpdate);
			toast.update(toastIdRef.current, {
				render: t('common:toast.success.refresh'),
				type: 'success',
				isLoading: false,
				autoClose: 3000,
			});
		} else {
			toast.dismiss(toastIdRef.current);
		}
		setLoading(false);
	};

	const handleDelete = async () => {
		if (!toDelete) return;
		setLoadingMessage('DELETE');
		const res = await api.delete(`/productions/${toDelete.batch}`);
		if (res?.status === 204) {
			toastIdRef.current = toast.loading(
				`${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`
			);
			fetchData();
		} else if (res?.status === 409) {
			toast.error(t('common:toast.error.delete'), { autoClose: 3000 });
		}
		setLoadingMessage(null);
		setToDelete(null);
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length === 0) return;
		setLoadingMessage('DELETE');
		const res = await api.post('/productions/delete', { batches: toDeleteBulk });
		if (res?.status === 204) {
			toastIdRef.current = toast.loading(
				`${t('common:toast.success.delete')} ${t('common:toast.loading.refresh')}`
			);
			tableRef.current?.resetRowSelection?.();
			fetchData(false, true);
		} else if (res?.status === 409) {
			toast.error(t('common:toast.error.delete'), { autoClose: 3000 });
		}
		setLoadingMessage(null);
		setToDeleteBulk([]);
	};

	const handleStart = async (row: any) => {
		setLoadingMessage('SAVE');
		const res = await api.post(`/productions/${row.batch}/start`);
		if (res?.status === 200) {
			toast.success(t('common:toast.success.productionStarted'), {
				autoClose: 3000,
			});
			fetchData(false, true);
		}
		setLoadingMessage(null);
	};

	const handleChanges = () => {
		toastIdRef.current = toast.loading(
			`${t('common:toast.success.changes')} ${t('common:toast.loading.refresh')}`
		);
		fetchData(false, true);
	};

	const columns: MRT_ColumnDef<any>[] = useMemo(
		() => [
			{
				header: t('common:tables.date'),
				id: 'date',
				accessorFn: (row: any) => parseISODay(row.date),
				Cell: ({ cell }: { cell: any }) =>
					Util.formatDate(cell.getValue(), 'medium'),
				filterVariant: 'date',
				filterFn: 'equals',
				columnFilterModeOptions: DATE_FILTER_OPTIONS,
			},
			{
				header: t('common:tables.reference'),
				accessorKey: 'reference',
				Cell: ({ cell }: { cell: any }) => (
					<button
						style={{
							background: 'none',
							border: 'none',
							padding: 0,
							color: 'inherit',
							cursor: 'pointer',
							textDecoration: 'underline',
						}}
						onClick={() => setToView(cell.row.original)}
					>
						{cell.getValue()}
					</button>
				),
				filterFn: 'contains',
				columnFilterModeOptions: TEXT_FILTER_OPTIONS,
			},
			{
				header: t('common:production.statusLabel'),
				accessorKey: 'status',
				Cell: ({ cell }: { cell: any }) => {
					const status = cell.getValue() || 'DRAFT';
					return (
						<Chip
							size="small"
							color={statusChipColor(status) as any}
							label={t(`common:production.status.${String(status).toLowerCase()}`)}
						/>
					);
				},
				filterVariant: 'autocomplete',
				filterFn: 'equals',
			},
			{
				header: t('common:production.bom'),
				accessorKey: 'bom_name',
				filterFn: 'contains',
				columnFilterModeOptions: TEXT_FILTER_OPTIONS,
			},
			{
				header: t('common:tables.observer'),
				accessorKey: 'client',
				filterVariant: 'autocomplete',
				filterFn: 'contains',
				columnFilterModeOptions: TEXT_FILTER_OPTIONS,
			},
			{
				header: t('common:production.productionDepot'),
				accessorKey: 'depot',
				filterVariant: 'autocomplete',
				filterFn: 'contains',
				columnFilterModeOptions: TEXT_FILTER_OPTIONS,
			},
			{
				header: t('common:production.scale'),
				accessorKey: 'scale',
				columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			},
			{
				header: t('common:production.expectedOut'),
				accessorKey: 'expected_output_qty',
				Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
				columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			},
			{
				header: t('common:production.actualOut'),
				accessorKey: 'output_qty',
				Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
				columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			},
			{
				header: t('common:production.inputCost'),
				accessorKey: 'input_amt',
				Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
				columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			},
			{
				header: t('common:production.outputValue'),
				accessorKey: 'output_amt',
				Cell: ({ cell }: { cell: any }) => Util.formatNumber(cell.getValue()),
				columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			},
			{
				header: t('common:tables.user'),
				accessorKey: 'user',
				filterVariant: 'autocomplete',
				filterFn: 'contains',
				columnFilterModeOptions: TEXT_FILTER_OPTIONS,
			},
			{
				header: t('common:tables.inStock'),
				accessorKey: 'is_stocked',
				Cell: ({ cell }: { cell: any }) =>
					cell.getValue() ? (
						<CheckCircleOutline sx={{ color: 'green' }} />
					) : (
						<CancelOutlined sx={{ color: 'red' }} />
					),
			},
		],
		[t]
	);

	return (
		<Box>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={LocalStorageKeys.PRODUCTION_RECORDS}
				posting="production"
					dataScope="production"
					header={t('common:production.headers.records')}
					data={data}
					columns={columns}
					totals={[
						{ key: 'output_qty', label: t('common:production.actualOut') },
						{ key: 'input_amt', label: t('common:production.inputCost') },
						{ key: 'output_amt', label: t('common:production.outputValue') },
					]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t('common:ui.tooltips.refreshData')}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Tooltip title={t('common:ui.tooltips.newProduction')}>
								<IconButton
									onClick={() => {
										if (allowOrWarn(canRecord(access, 'production'))) {
											setNewRecord(true);
										}
									}}
									color="success"
								>
									<AddCircleOutline />
								</IconButton>
							</Tooltip>
							{table.getSelectedRowModel().flatRows.length > 0 && (
								<Tooltip title={t('common:ui.tooltips.deleteSelection')}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canDrop(access, 'production', 'delete_bulk'))) {
												const rowsBatches = [
													...new Set(
														table
															.getSelectedRowModel()
															.flatRows.map((row: any) => row.original.batch)
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(
														table.getSelectedRowModel().flatRows[0].original
													);
												} else {
													tableRef.current = table;
													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error"
									>
										<Delete />
									</IconButton>
								</Tooltip>
							)}
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => {
						const status = row.original.status || 'DRAFT';
						return [
							<MenuItem
								key="view"
								onClick={() => {
									setToView(row.original);
									closeMenu();
								}}
								sx={{ color: 'info.main' }}
							>
								<ListItemIcon sx={{ color: 'info.main' }}>
									<Visibility />
								</ListItemIcon>
								{t('common:ui.buttons.view')}
							</MenuItem>,
							status !== 'FINALIZED' ? (
								<MenuItem
									key="open"
									onClick={() => {
										setToEdit(row.original);
										closeMenu();
									}}
									sx={{ color: 'primary.main' }}
								>
									<ListItemIcon sx={{ color: 'primary.main' }}>
										<Edit />
									</ListItemIcon>
									{t('common:ui.buttons.edit')}
								</MenuItem>
							) : null,
							status === 'DRAFT' ? (
								<MenuItem
									key="start"
									onClick={() => {
										if (allowOrWarn(canAmend(access, 'production'))) {
											handleStart(row.original);
										}
										closeMenu();
									}}
									sx={{ color: 'warning.main' }}
								>
									<ListItemIcon sx={{ color: 'warning.main' }}>
										<PlayArrow />
									</ListItemIcon>
									{t('common:production.startRun')}
								</MenuItem>
							) : null,
							status === 'RUNNING' || status === 'DRAFT' ? (
								<MenuItem
									key="finalize"
									onClick={() => {
										if (allowOrWarn(canAmend(access, 'production'))) {
											setToFinalize(row.original);
										}
										closeMenu();
									}}
									sx={{ color: 'success.main' }}
								>
									<ListItemIcon sx={{ color: 'success.main' }}>
										<DoneAll />
									</ListItemIcon>
									{t('common:production.finalize')}
								</MenuItem>
							) : null,
							<MenuItem
								key="delete"
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'production'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{ color: 'error.main' }}
							>
								<ListItemIcon sx={{ color: 'error.main' }}>
									<Delete />
								</ListItemIcon>
								{t('common:ui.buttons.delete')}
							</MenuItem>,
						];
					}}
				/>
			</LocalizationProvider>

			<ProductionModal
				isOpen={newRecord || !!toEdit}
				toggle={() => {
					setNewRecord(false);
					setToEdit(null);
				}}
				bundle={toEdit}
				onSuccess={handleChanges}
			/>

			<ProductionModal
				isOpen={!!toFinalize}
				toggle={() => setToFinalize(null)}
				bundle={toFinalize}
				finalizeMode
				onSuccess={handleChanges}
			/>

			{toView && (
				<ProductionReport
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			)}

			<ConfirmDialog
				onConfirm={toDelete ? handleDelete : handleBulkDelete}
				onCancel={() => {
					toDelete ? setToDelete(null) : setToDeleteBulk([]);
				}}
				isOpen={!!toDelete || toDeleteBulk.length > 0}
				setIsOpen={() => {}}
				title={
					<>
						<DeleteOutline sx={{ color: 'red' }} />{' '}
						{t('common:dialog.deleteConfirmation')}
					</>
				}
				message={
					toDelete
						? t('common:production.deleteSingle')
						: t('common:production.deleteBulk', {
								count: Number(toDeleteBulk.length),
						  })
				}
				confirmBtnColor="error"
				confirmBtnVariant="contained"
				confirmBtnText={<>{t('common:ui.buttons.delete')}</>}
			/>
		</Box>
	);
};

export default ProductionRecords;
